// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo } from 'react';
import {
    ChartOutput,
    WidgetBarOutput,
    WidgetCounterOutput,
    WidgetDonutOutput,
    WidgetGaugeOutput,
    WidgetLineOutput,
    WidgetOutput,
    WidgetRadarOutput,
    WidgetTextOutput,
} from 'thehive-sdk';
import { classList, useLocalMessage } from '@Helpers/';
import { useDashboard } from '@Hooks/useDashboard';
import { WidgetIcon } from '@Organisms/Dashboard/DashboardView/constants';
import CounterDrawer from '@Organisms/Dashboard/WidgetManage/Counter/CounterDrawer';
import BarDrawer from '@Organisms/Dashboard/WidgetManage/Bar/BarDrawer';
import DonutDrawer from '@Organisms/Dashboard/WidgetManage/Donut/DonutDrawer';
import LineDrawer from '@Organisms/Dashboard/WidgetManage/Line/LineDrawer';
import TextDrawer from '@Organisms/Dashboard/WidgetManage/Text/TextDrawer';
import DeleteButton from '@Atoms/Buttons/DeleteButton/DeleteButton';
import { BarWidget } from '@Molecules/Widgets/BarWidget';
import { DonutWidget } from '@Molecules/Widgets/DonutWidget';
import { LineWidget } from '@Molecules/Widgets/LineWidget';
import { CounterWidget } from '@Molecules/Widgets/CounterWidget';
import { TextWidget } from '@Molecules/Widgets/TextWidget';
import RadarDrawer from '@Organisms/Dashboard/WidgetManage/Radar/RadarDrawer';
import { RadarWidget } from '@Molecules/Widgets/RadarWidget';
import GaugeDrawer from '@Organisms/Dashboard/WidgetManage/Gauge/GaugeDrawer';
import { GaugeWidget } from '@Molecules/Widgets/GaugeWidget';
import { UseQueryResult } from 'react-query';

import './WidgetView.scss';

const WidgetEditDrawer = {
    bar: (widget: WidgetOutput): ReactElement => <BarDrawer data={widget as WidgetBarOutput} />,
    donut: (widget: WidgetOutput): ReactElement => <DonutDrawer data={widget as WidgetDonutOutput} />,
    line: (widget: WidgetOutput): ReactElement => <LineDrawer data={widget as WidgetLineOutput} />,
    radar: (widget: WidgetOutput): ReactElement => <RadarDrawer data={widget as WidgetRadarOutput} />,
    text: (widget: WidgetOutput): ReactElement => <TextDrawer data={widget as WidgetTextOutput} />,
    counter: (widget: WidgetOutput): ReactElement => <CounterDrawer data={widget as WidgetCounterOutput} />,
    gauge: (widget: WidgetOutput): ReactElement => <GaugeDrawer data={widget as WidgetGaugeOutput} />,
    default: (): null => null,
};

const WidgetViewMode = {
    bar: (widget: WidgetOutput, query?: UseQueryResult<ChartOutput[]>): ReactElement => (
        <BarWidget widget={widget} query={query} />
    ),
    donut: (widget: WidgetOutput, query?: UseQueryResult<ChartOutput[]>): ReactElement => (
        <DonutWidget widget={widget} query={query} />
    ),
    line: (widget: WidgetOutput, query?: UseQueryResult<ChartOutput[]>): ReactElement => (
        <LineWidget widget={widget} query={query} />
    ),
    counter: (widget: WidgetOutput, query?: UseQueryResult<ChartOutput>): ReactElement => (
        <CounterWidget widget={widget} query={query} />
    ),
    text: (widget: WidgetOutput, query?: UseQueryResult<ChartOutput>): ReactElement => (
        <TextWidget widget={widget} query={query} />
    ),
    radar: (widget: WidgetOutput, query?: UseQueryResult<ChartOutput[]>): ReactElement => (
        <RadarWidget widget={widget} query={query} />
    ),
    gauge: (widget: WidgetOutput, query?: UseQueryResult<ChartOutput>): ReactElement => (
        <GaugeWidget widget={widget} query={query} />
    ),
    default: (): ReactElement => <>test</>,
};

export interface WidgetViewProps {
    widget: WidgetOutput;
    query?: UseQueryResult<any, unknown>;
    className?: string;
}

export const WidgetView = ({ widget, query, className = '' }: WidgetViewProps): ReactElement => {
    const i = useLocalMessage();

    const { dashboard, setDashboard, editMode } = useDashboard();
    const { options, id, type } = widget;

    const deleteWidget = (): void => {
        setDashboard({
            ...dashboard,
            definition: {
                ...dashboard.definition,
                items: dashboard.definition.items.map((container) => ({
                    ...container,
                    items: container.items.filter((widget) => widget.id !== id),
                })),
            },
        });
    };

    const EditDrawer = (): ReactElement => (WidgetEditDrawer[type] || WidgetEditDrawer.default)(widget);
    const Widget = useMemo(
        () => (WidgetViewMode[type] || WidgetViewMode.default)(widget, query),
        [type, widget, query],
    );

    return (
        <div className={classList('widget-view', className)}>
            <header className="widget-view__header">
                <h2 className="widget-view__title">
                    {WidgetIcon[type]} {options.title || i('app.dashboard.widget.notitle')}
                </h2>
                <span className="widget-view__actions">
                    {editMode && (
                        <>
                            <EditDrawer />
                            <DeleteButton
                                label={i('app.dashboard.widget.delete.modal.content')}
                                onConfirm={(): void => deleteWidget()}
                            />
                        </>
                    )}
                </span>
            </header>
            <div className="widget-view__content">{Widget}</div>
        </div>
    );
};
